import "src/utilities/wdyr";

import "regenerator-runtime/runtime"; // needed for react-table with swc because it has async/await and transpiling it is not working
// essential polyfills come first and are part of the main bundle
import "src/utilities/polyfills/object";
import "focus-visible";
import { AuthGateway } from "@components/AuthGateway";
import { LoadingPage } from "@components/LoadingPage";
import { css, Global } from "@emotion/react";
import { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";
import "react-datepicker/dist/react-datepicker.css";
import "react-dropdown/style.css";
import Big from "big.js";
import Cookies from "js-cookie";
import { AppProps } from "next/app";
import { NextParsedUrlQuery } from "next/dist/server/request-meta";
import Head from "next/head";
import { useRouter } from "next/router";
import Script from "next/script";
import { Fragment, useEffect } from "react";
import Modal from "react-modal";
import superjson from "superjson";

import { CookieKey } from "@every.org/common/src/entity/types/cookies";
import { IotsCodecError } from "@every.org/common/src/errors/IotsCodecError";
import { isBig } from "@every.org/common/src/helpers/big";
import {
  ClientRouteName,
  getClientRouteFromPath,
} from "@every.org/common/src/helpers/clientRoutes";
import { assertEnvPresent } from "@every.org/common/src/helpers/getEnv";
import { helloMeRouteSpec } from "@every.org/common/src/routes/me";

import { modalGlobalCss } from "src/components/Modal";
import { NoScript } from "src/components/app/NoScript";
import { VendorScripts } from "src/components/app/VendorScripts";
import { WelcomeModal } from "src/components/layout/WelcomeModal";
import { ContextProviders } from "src/context/ContextProviders";
import { parseNextRoute } from "src/hooks/useEdoRouter";
import { usePageTransition } from "src/hooks/usePageTransition";
import { EdoErrorPage } from "src/pages/EdoErrorPage";
import { globalCss } from "src/theme/GlobalStyle";
import {
  installOptionalPolyfills,
  installRequiredPolyfills,
} from "src/utilities/polyfills";
import { deserializeProps } from "src/utilities/ssr/superjson";
import { trackPageChange } from "src/utility/analytics";
import { queryApi } from "src/utility/apiClient";
import { getLocalStorage, LocalStorageKey } from "src/utility/localStorage";
import { logger, LogServices } from "src/utility/logger";
import { getWindow } from "src/utility/window";

// These routes call trackPageChange within their component for greater
// granularity.
const TRACK_IN_COMPONENT = new Set([
  ClientRouteName.NONPROFIT_OR_CAUSE,
  ClientRouteName.USER,
  ClientRouteName.USER_JOINS,
  ClientRouteName.USER_LIKES,
  ClientRouteName.USER_LISTS,
]);

superjson.registerCustom<Big, string>(
  {
    isApplicable: (value): value is Big => {
      return isBig(value);
    },
    serialize: (value) => value.toString(),
    deserialize: (value) => new Big(value),
  },
  "big.js"
);

function logAndShowStartupError(error: Error) {
  logger.error({
    message: "A fatal error occurred loading the app.",
    error,
  });
  const body = getWindow()?.document.getElementsByTagName("body")[0];
  if (body) {
    body.innerHTML = /* html */ `
    <div style="height: 100vh; width: 100%; display: flex; align-items: center; justify-content: center; font-family: Arial; sans-serif; font-weight: normal;">
      <div style="max-width: 600px; text-align: center;">
        <h3 style="font-weight: normal;">
          Every.org is not working on this browser! Try reloading, using a newer browser, or feel
          free to <a
          href="https://support.every.org/hc/en-us/requests/new">contact us</a>
        </h3>
      </div>
    </div>
    `;
  }
}

const DefaultHead = () => (
  <Fragment>
    <VendorScripts />
    <Head>
      <meta charSet="utf-8" />
      {/* START Icons */}
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-touch-icon.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon-16x16.png"
      />
      <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#2bd7b0" />
      {/* END Icons */}
      <link rel="preconnect" href="https://res.cloudinary.com" />
      <link
        rel="preconnect"
        href={assertEnvPresent(
          process.env.NEXT_PUBLIC_API_ORIGIN,
          "NEXT_PUBLIC_API_ORIGIN"
        )}
      />

      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#ffffff" />
      {/* Open Graph and Facebook properties. Do not add any of the dynamically
    controlled tags like og:title, og:description and og:image specified by
    MetaTags in DefaultPageLayout.tsx otherwise they canot be properly
    controlled. */}
      <meta property="og:site_name" content="Every.org" />
      <meta
        property="fb:app_id"
        content={assertEnvPresent(
          process.env.NEXT_PUBLIC_FB_ID,
          "NEXT_PUBLIC_FB_ID"
        )}
      />
      <meta property="og:type" content="website" />
      {/* Twitter tags
    Ideally the alt text would also be dynamically controlled and relevant to the image. */}
      <meta
        name="twitter:image:alt"
        content="Every.org helps people support a wide variety of causes."
      />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@everydotorg" />
      <meta name="twitter:creator" content="@everydotorg" />
      {/* manifest.json provides metadata used when your web app is installed on
      a user's mobile device or desktop. See
      https://developers.google.com/web/fundamentals/web-app-manifest/ */}
      <link rel="manifest" href="/manifest.json" />
    </Head>

    {/* Stripe suggests including their library on every page
    to assist their fraud prevention algorithms. Loaded from the "dahlia"
    release train (not the classic /v3/ URL) to match the version
    @stripe/stripe-js is pinned to - see utility/stripe.ts - otherwise
    Stripe.js logs a version-mismatch warning. */}
    <Script async defer src="https://js.stripe.com/dahlia/stripe.js" />
    <Script
      async
      defer
      id="plaid-js"
      src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"
    />
    <Script
      id="turnstile-js"
      src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
    />
    <NoScript />
  </Fragment>
);
const fontCss = css`
  @font-face {
    font-family: "Basis Grotesque";
    src: url("/fonts/BasisGrotesqueWeb/BasisGrotesque-Bold-Pro.woff2")
        format("woff2"),
      url("/fonts/BasisGrotesqueWeb/BasisGrotesque-Bold-Pro.woff")
        format("woff"),
      url("/fonts/BasisGrotesqueWeb/BasisGrotesque-Bold-Pro.ttf")
        format("truetype");
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: "Basis Grotesque";
    src: url("/fonts/BasisGrotesqueWeb/BasisGrotesque-Medium-Pro.woff2")
        format("woff2"),
      url("/fonts/BasisGrotesqueWeb/BasisGrotesque-Medium-Pro.woff")
        format("woff"),
      url("/fonts/BasisGrotesqueWeb/BasisGrotesque-Medium-Pro.ttf")
        format("truetype");
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: "Basis Grotesque";
    src: url("/fonts/BasisGrotesqueWeb/BasisGrotesque-Regular-Pro.woff2")
        format("woff2"),
      url("/fonts/BasisGrotesqueWeb/BasisGrotesque-Regular-Pro.woff")
        format("woff"),
      url("/fonts/BasisGrotesqueWeb/BasisGrotesque-Regular-Pro.ttf")
        format("truetype");
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
`;
const MyApp = ({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Component,
  pageProps,
}: AppProps) => {
  useEffect(() => {
    Modal.setAppElement("#__next");
    installOptionalPolyfills();
    installRequiredPolyfills().catch((error) => logAndShowStartupError(error));
  }, []);

  useEffect(() => {
    // Only send the daily heartbeat for real logged-in users. Guests also carry
    // a SESSION_EXISTS cookie, but /me/hello is authenticated-only: it 401s for
    // a guest, and the API client treats any 401 as a logout — which would drop
    // the guest's session on load. USER_STATUS is set only for logged-in
    // (non-guest) users.
    if (
      !Cookies.get(CookieKey.SESSION_EXISTS) ||
      !Cookies.get(CookieKey.USER_STATUS)
    ) {
      return;
    }
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
    const ls = getLocalStorage();
    if (ls?.getItem(LocalStorageKey.USER_HELLO_DATE) === today) {
      return;
    }
    queryApi(helloMeRouteSpec, {
      routeTokens: {},
      body: { userLocalDate: today },
      queryParams: {},
    })
      .then(() => {
        ls?.setItem(LocalStorageKey.USER_HELLO_DATE, today);
      })
      .catch(() => {
        // best-effort; ignore errors
      });
  }, []);

  const router = useRouter();
  const props = deserializeProps(pageProps) as any;

  useEffect(() => {
    const handleRouteChange = (
      nextRoute: string,
      query: NextParsedUrlQuery
    ) => {
      const parsedRoute = parseNextRoute(nextRoute, query);
      const clientRoute = getClientRouteFromPath(parsedRoute, nextRoute);
      if (
        clientRoute &&
        !TRACK_IN_COMPONENT.has(clientRoute as ClientRouteName)
      ) {
        trackPageChange(clientRoute);
      }
    };
    if (router.isReady) {
      handleRouteChange(router.pathname, router.query);
    }
  }, [router.pathname, router.query, router.isReady]);

  const pageTransition = usePageTransition();

  return (
    <SentryErrorBoundary
      beforeCapture={(scope, error) => {
        if (error?.name === "ChunkLoadError") {
          // happens frequenty when we deploy and old chunks are purged; we will
          // refresh in onError callback so this is jarring but not fatal
          scope.setLevel("info");
        } else {
          scope.setLevel("fatal");
        }
      }}
      onError={(error, componentStack) => {
        const logFn =
          error.name === "ChunkLoadError" ? logger.info : logger.fatal;
        if (error instanceof IotsCodecError) {
          logFn({
            message:
              "An uncaught io-ts validation error occurred in the React app.",
            data: { errorMessages: error.data.errorMessages },
            excludeLogServices: [LogServices.SENTRY], // already sent by error boundary
          });
        } else {
          logFn({
            message: "An uncaught error occurred in the React app.",
            error,
            excludeLogServices: [LogServices.SENTRY], // already sent by error boundary
          });
        }
        const window = getWindow();
        if (window && error.name === "ChunkLoadError") {
          window.location.reload();
        }
      }}
      fallback={({ error }) =>
        error.name !== "ChunkLoadError" ? <EdoErrorPage /> : <div />
      }
    >
      <DefaultHead />
      <Global styles={[globalCss, modalGlobalCss, fontCss]} />
      <ContextProviders initialData={props?.initialData || {}}>
        <AuthGateway>
          {pageTransition ? <LoadingPage /> : <Component {...pageProps} />}
          <WelcomeModal />
        </AuthGateway>
      </ContextProviders>
    </SentryErrorBoundary>
  );
};

export default MyApp;
